import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { permissionApi, roleApi } from '../api'

const roleKeys = {
  all: ['roles'] as const,
  lists: () => [...roleKeys.all, 'list'] as const,
  details: () => [...roleKeys.all, 'detail'] as const,
  detail: (id: string) => [...roleKeys.details(), id] as const,
}

const permissionKeys = {
  all: ['permissions'] as const,
}

export function useRoles() {
  return useQuery({
    queryKey: roleKeys.lists(),
    queryFn: () => roleApi.getAllRoles(),
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useRole(id: string) {
  return useQuery({
    queryKey: roleKeys.detail(id),
    queryFn: () => roleApi.getRoleById(id),
    enabled: !!id,
  })
}

export function usePermissions() {
  return useQuery({
    queryKey: permissionKeys.all,
    queryFn: () => permissionApi.getAllPermissions(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useAttachPermission() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      roleId,
      permission,
    }: {
      roleId: string
      permission: string
    }) => roleApi.attachPermission(roleId, permission),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() })
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId) })
      toast({ title: 'Permission added successfully', variant: 'default' })
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to add permission', 
        description: error?.response?.data?.message || error.message,
        variant: 'destructive' 
      })
    },
  })
}

export function useDetachPermission() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      roleId,
      permission,
    }: {
      roleId: string
      permission: string
    }) => roleApi.detachPermission(roleId, permission),
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() })
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId) })
      toast({ title: 'Permission removed successfully', variant: 'default' })
    },
    onError: (error: any) => {
        toast({ 
            title: 'Failed to remove permission', 
            description: error?.response?.data?.message || error.message,
            variant: 'destructive' 
          })
    },
  })
}

export function useCreateRole() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: {
      name: string
      description?: string
      permissions?: string[]
    }) => roleApi.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() })
      toast({ title: 'Role created successfully', variant: 'default' })
    },
    onError: (error: any) => {
        toast({ 
            title: 'Failed to create role', 
            description: error?.response?.data?.message || error.message,
            variant: 'destructive' 
          })
    },
  })
}

export function useUpdateRole() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: { name?: string; description?: string; isEnabled?: boolean }
    }) => roleApi.updateRole(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() })
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(id) })
      toast({ title: 'Role updated successfully', variant: 'default' })
    },
    onError: (error: any) => {
        toast({ 
            title: 'Failed to update role', 
            description: error?.response?.data?.message || error.message,
            variant: 'destructive' 
          })
    },
  })
}

export function useDeleteRole() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => roleApi.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() })
      toast({ title: 'Role deleted successfully', variant: 'default' })
    },
    onError: (error: any) => {
        toast({ 
            title: 'Failed to delete role', 
            description: error?.response?.data?.message || error.message,
            variant: 'destructive' 
          })
    },
  })
}
